import { whenDomReady } from "@allwyn/utils/document-ready";
import { ResponsiveUtils } from "@allwyn/utils/responsive-utils";
import { TimeoutTimer } from "@allwyn/utils/timer";
import { HrajSRozumemAnalytics } from "../utils/analytics-hraj-s-rozumem";

const SELECTORS = {
    root: ".js-carousel-widget",
    slide: ".js-carousel-widget-slide",
    image: ".js-carousel-widget-image",
    menuItem: ".js-carousel-widget-menu-item",
    activeSlide: "carousel-widget__slide--active",
    active: "carousel-widget__menu-item--active",
    hover: "carousel-widget--hover",
    darkSlide: "carousel-widget__slide--dark",
    darkMenuItem: "carousel-widget__menu-item--dark",
};

const CONFIG = {
    slideTimeout: 6000,
    parallaxCoefficient: 0.05,
    analyticsName: "hraj-s-rozumem.carousel",
};

function extractSlideData(slideElement: HTMLElement) {
    const slideImg = slideElement.querySelector<HTMLImageElement>(SELECTORS.image);
    const codename = slideElement.dataset.aCodename;
    const index = slideElement.dataset.aIndex;
    const src = slideImg?.src || slideImg?.dataset.src;
    return { codename, index, src };
}

function setupParallax(root: HTMLElement, images: NodeListOf<HTMLElement>) {
    let lastPosition = window.scrollY;

    const updateParallax = (y: number) => {
        const yPos = y * CONFIG.parallaxCoefficient;
        root.style.transform = `translate3d(0, ${yPos}px, 0)`;
        images.forEach((image) => {
            image.style.transform = `translate3d(0, ${yPos}px, 0)`;
        });
    };

    const onScroll = () => {
        lastPosition = window.scrollY;
        requestAnimationFrame(() => updateParallax(lastPosition));
    };

    window.addEventListener("scroll", onScroll, { passive: true });
}

function setNavigationColor(slide: HTMLElement | null, menuItems: NodeListOf<HTMLElement>) {
    const isDark = slide?.classList.contains(SELECTORS.darkSlide) ?? false;
    menuItems.forEach((item) => {
        item.classList.toggle(SELECTORS.darkMenuItem, isDark);
    });
}

function setupAnalytics(slide: HTMLElement, pushedSlides: number[]) {
    const slideId = slide.dataset.slideId;
    const slideIdNum = Number(slideId);

    if (!slideId || pushedSlides.includes(slideIdNum)) return;

    pushedSlides.push(slideIdNum);
    const { codename, src, index } = extractSlideData(slide);

    if (codename && src && index) {
        HrajSRozumemAnalytics.actionImpressPromo(
            codename,
            CONFIG.analyticsName,
            src,
            `${CONFIG.analyticsName}.${index}`
        );
    }

    slide.addEventListener("click", () => {
        const { codename, src, index } = extractSlideData(slide);
        if (codename && src && index) {
            HrajSRozumemAnalytics.actionShowPromo(
                codename,
                CONFIG.analyticsName,
                src,
                `${CONFIG.analyticsName}.${index}`
            );
        }
    });
}

function initCarousel() {
    const root = document.querySelector<HTMLElement>(SELECTORS.root);
    if (!root) return;
    root.style.setProperty("--carousel-widget-slide-time", `${CONFIG.slideTimeout}ms`);

    const images = root.querySelectorAll<HTMLElement>(SELECTORS.image);
    const slides = root.querySelectorAll<HTMLElement>(SELECTORS.slide);
    const menuItems = root.querySelectorAll<HTMLElement>(SELECTORS.menuItem);

    if (slides.length === 0) return;

    const firstSlide = slides[0];
    const pushedSlides: number[] = [];

    setNavigationColor(firstSlide, menuItems);
    setupAnalytics(firstSlide, pushedSlides);

    if (root.dataset.disableParallax === undefined) {
        setupParallax(root, images);
    }

    if (slides.length <= 1) return;

    let timer = new TimeoutTimer(goToNextSlide, CONFIG.slideTimeout);

    const resetTimer = () => {
        timer.clear();
        timer = new TimeoutTimer(goToNextSlide, CONFIG.slideTimeout);
        if (root.classList.contains(SELECTORS.hover)) timer.pause();
    };

    function activateSlide(target: HTMLElement) {
        const slideId = target.dataset.slideId;
        if (!slideId) return;

        slides.forEach((s) => s.classList.remove(SELECTORS.activeSlide));
        menuItems.forEach((m) => m.classList.remove(SELECTORS.active));

        target.classList.add(SELECTORS.activeSlide);
        setupAnalytics(target, pushedSlides);

        const menuItem = root.querySelector<HTMLElement>(`${SELECTORS.menuItem}[data-slide="${slideId}"]`);
        menuItem?.classList.add(SELECTORS.active);

        resetTimer();
    }

    function goToNextSlide() {
        const current = root.querySelector<HTMLElement>(`.${SELECTORS.activeSlide}`);
        const next = (current?.nextElementSibling as HTMLElement) || firstSlide;
        activateSlide(next);
        setNavigationColor(next, menuItems);
    }

    if (ResponsiveUtils.breakpointUp("sm")) {
        root.addEventListener("mouseenter", () => {
            root.classList.add(SELECTORS.hover);
            timer.pause();
        });
        root.addEventListener("mouseleave", () => {
            root.classList.remove(SELECTORS.hover);
            timer.resume();
        });

        menuItems.forEach((item) => {
            item.addEventListener("click", () => {
                if (item.classList.contains(SELECTORS.active)) return;
                const target = root.querySelector<HTMLElement>(
                    `${SELECTORS.slide}[data-slide-id="${item.dataset.slide}"]`
                );
                if (target) {
                    activateSlide(target);
                    setNavigationColor(target, menuItems);
                }
            });
        });
    }
}

await whenDomReady();
initCarousel();
